/**
 * CardDetailModal.tsx — WEB.4.UI.3
 *
 * Two-stage reveal:
 *   1. Cover (380×651): type-cover JPG, "CLICK TO REVEAL" hint
 *   2. Revealed (820×560): art left + body/effects/footer right
 *
 * Click cover → flipReveal (cover → revealed).
 * Esc / click-outside → closeCardDetail (no play).
 *
 * Footer button logic per card type:
 *   Event isImmediate=true  → single ACKNOWLEDGE (playCard + close)
 *   Event isImmediate=false → PLAY + CANCEL
 *   Policy buyCost > 0      → PAY Nt (disabled if can't afford) + CANCEL
 *   Policy buyCost = 0      → PLAY + CANCEL
 *   Crisis (Mitigate type)  → ACKNOWLEDGE
 *
 * VISIBLE WHEN: detailModalCard !== null
 * TEST PATH: land on Event tile → modal opens with cover → click → reveal → play
 */

import type { CardEffect } from "@gdg/shared";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { playSfx } from "../../audio/audio.js";
import { useI18n } from "../../i18n/useI18n.js";
import { getCardById, useGameStore } from "../../store/game-store.js";
import styles from "./CardDetailModal.module.css";

// Effect kinds + target that the WEB.4.UI.3 playCard pipeline supports end-to-end.
// Anything else → Play/Pay button disabled (card cannot be consumed yet).
// Sync with _isEffectSupportedInPlayCard in game-store.ts.
const SUPPORTED_EFFECT_KINDS = new Set<string>([
  "TokenDelta",
  "EnergyDelta",
  "PollutionDelta",
  "StealEnergy",
  // WEB.4.UI.8a: AuraApply now registers into persistent auraState.
  "AuraApply",
  // WEB.4.UI.15 (gap #3.A) — Immunity + CancelDisaster register into persistent
  // immunityState (UN_Green_Pact_Support, Microgrids, Disaster_Preparedness_Fun).
  "Immunity",
  "CancelDisaster",
  // WEB.4.UI.16 — BuildCostMod queues a PEQ entry (HalveNextTokenLoss for
  // Climate_Adaptation_Strategy; NextBuildCostBump for build modifiers).
  "BuildCostMod",
  // DC-1 Bug #7: DrawCard is now fully wired — countProvider seeded from
  // facilityOwnership, deck seeded from Policy pool, post-pipeline CIS diff
  // propagated as pendingFreeDrawCard. Sync with _isEffectSupportedInPlayCard.
  "DrawCard",
  // NOTE: Trigger is intentionally NOT in this set — Disaster_Relief_Fund is
  // `isImmediate: false` and sits in hand reactively. Trigger dispatch happens
  // at the destruction site (confirmTilePick), not via the Play button.
  // R20-GD-1: TurnBlock (Drought) handled inline in playCard; no pipeline needed.
  "TurnBlock",
  // R20-GD-7: GiveCard (Policy_Exchange swap arm) routed via confirmTarget:StealCard
  // which sets pendingGiveCard; no separate pipeline pass needed.
  "GiveCard",
]);
function _isCardPlayable(effects: ReadonlyArray<CardEffect>): boolean {
  // Codex R3 HIGH 1 (WEB.4.UI.6): OpponentChosen-targeted cards ARE playable —
  // clicking Play opens TargetPicker (handled by store playCard action).
  // The picker then handles per-effect-kind support (defers chained
  // FacilityTransfer/Destroy/Decommission, plays StealEnergy/TokenDelta/etc).
  // Previously this returned false for ALL OpponentChosen cards, blocking
  // the entire UI entry path to TargetPicker.
  const OPPONENT_PICKER_KINDS = new Set([
    ...SUPPORTED_EFFECT_KINDS,
    "StealCard",
    "Destroy",
    "Decommission",
    "FacilityTransfer",
    "DiceOutcome", // codex R4: Mercenaries + Spy_Sabotage_II use DiceOutcome+OpponentChosen
    // R20-GD-7: GiveCard@OpponentChosen (Policy_Exchange swap arm).
    "GiveCard",
  ]);
  // WEB.4.UI.X.4 (Net_Zero fix): the prior same-scope multi-AuraApply gate
  // is REMOVED. AuraManager.register now dedupes by
  // (instanceId, scope, categoryFilter, facilityFilter) so Net_Zero_Movement's
  // two Toll effects (Fossil mult=0.5 + Solar mult=2.0) both register cleanly.
  return effects.every((e) => {
    if (e.target === "OpponentChosen") return OPPONENT_PICKER_KINDS.has(e.kind);
    // WEB.4.UI.18 — multi-tile global sweeps (RngBranch/Decommission with
    // affectsAll=true + target=Global) are supported end-to-end. Destroy is
    // intentionally excluded — no resolveDestroyMultiTile exists yet, so a
    // future Destroy+affectsAll card would pass this gate and silently
    // Skip+consume. Re-add only after the resolver lands.
    if (
      (e.kind === "RngBranch" || e.kind === "Decommission") &&
      e.affectsAll === true &&
      e.target === "Global"
    ) {
      return true;
    }
    // WEB.4.PT.17 — single-target Destroy/Decommission/RngBranch with
    // target in {Self, NearestPlayer} are now supported end-to-end.
    // Sync with _isEffectSupportedInPlayCard in game-store.ts.
    if (
      (e.kind === "Destroy" || e.kind === "Decommission" || e.kind === "RngBranch") &&
      e.affectsAll !== true &&
      (e.target === "Self" || e.target === "NearestPlayer")
    ) {
      return true;
    }
    return SUPPORTED_EFFECT_KINDS.has(e.kind);
  });
}

// ---------------------------------------------------------------------------
// Cover image path helper
// ---------------------------------------------------------------------------

/** Boon Events — Event cards whose mechanics are positive/neutral, not disasters.
 *  Game design: all Events share the crisis-tinted cover (the BACK of the
 *  physical card is the same regardless of polarity). To avoid misleading
 *  framing for positive Events, render a corner OVERLAY indicator ("+ BOON")
 *  instead of swapping covers — keeps physical-card parity while signaling
 *  polarity. Per fact-check audit HIGH 1 + user direction 2026-05-11.
 *  TODO(WEB.4.UI.5+): migrate hardcoded list to cards.json `polarity` field. */
const BOON_EVENT_IDS = new Set<string>([
  "Foreign_Investment_E1",
  "Foreign_Investment_E2",
  "Industrial_Revolution",
  "Nuclear_Treaty_Signed",
  "Silicon_Valley_Startup",
  "Solar_Panel_Oversupply",
  "Steam_Engine_Breakthrough",
  "Oil_Boom_Prosperity",
  "Green_Pioneers",
  "International_Green_Funding",
  "Paris_Climate_Agreement",
  "Energy_Tourism",
]);

function isBoonEvent(cardType: string, cardId: string): boolean {
  return cardType === "Event" && BOON_EVENT_IDS.has(cardId);
}

/** Returns the public path for the type cover JPG.
 *  Event/Crisis → crisis cover (brown). Policy → policy cover (navy).
 *  Mitigate → mitigate cover (green). All Events use the same crisis cover
 *  regardless of polarity — polarity is signaled via overlay indicator,
 *  NOT by swapping covers (preserves physical-card parity). */
function getCoverPath(cardType: string): string {
  const folder = cardType === "Policy" ? "policy" : cardType === "Mitigate" ? "mitigate" : "crisis";
  return `/images/cards/source/${folder}/cover.jpg`;
}

/**
 * R7 #5/#7 — Resolve card artwork → public image path.
 *
 * cards.json `artwork` field has three shapes:
 *   1. Named file at root: "Cyberattack", "Pipeline_Explosion" etc. → /images/cards/<name>.jpg
 *   2. Numeric/named with path already: "source/crisis/4", "4", "10 (4)", "12 (5)"
 *      These map to source/{crisis|policy|mitigate}/ based on cardType.
 *      Some have ".jpg" embedded ("3.jpg", "4.jpg") — strip before appending.
 *   3. Explicit subfolder prefix in artwork: "mitigate/1.jpg", "policy/31 (3)"
 *      These already encode the subfolder — use under source/.
 *   4. policy-N.jpg shorthand: "policy-9.jpg", "policy-7.jpg"
 *      These map to source/policy/<N>.jpg.
 *
 * All paths are under /images/cards/ (Vite serves assets/ as /).
 */
function resolveArtPath(artwork: string | null | undefined, cardType: string): string | null {
  if (artwork === null || artwork === undefined || artwork === "") return null;

  // Strip leading/trailing whitespace (some fields have trailing space)
  const raw = artwork.trim();
  if (raw === "") return null;

  // Named files at root — no path separators, no .jpg suffix, starts with
  // uppercase letter OR digit (e.g. "1973_Oil_Crisis"). These map directly to
  // /images/cards/<artwork>.jpg (the hand-named card art files).
  // R13 #13: original regex (/^[A-Z].../) excluded digit-leading names like
  // "1973_Oil_Crisis", causing the fallback to build a wrong source/ path.
  if (/^[A-Z0-9][A-Za-z0-9_]+$/.test(raw)) {
    return `/images/cards/${raw}.jpg`;
  }

  // Explicit mitigate/ or policy/ prefix already in value (e.g. "mitigate/1.jpg", "policy/31 (3)")
  if (raw.startsWith("mitigate/") || raw.startsWith("policy/")) {
    const withoutExt = raw.replace(/\.jpg$/i, "");
    return `/images/cards/source/${withoutExt}.jpg`;
  }

  // policy-N.jpg shorthand ("policy-9.jpg", "policy-7.jpg")
  const policyDash = raw.match(/^policy-(\d+)(?:\.jpg)?$/i);
  if (policyDash) {
    return `/images/cards/source/policy/${policyDash[1]}.jpg`;
  }

  // Strip embedded .jpg extension for numeric values ("3.jpg" → "3", "10.jpg" → "10")
  const stripped = raw.replace(/\.jpg$/i, "");

  // Determine source subfolder from cardType
  const folder = cardType === "Policy" ? "policy" : cardType === "Mitigate" ? "mitigate" : "crisis";
  return `/images/cards/source/${folder}/${stripped}.jpg`;
}

// ---------------------------------------------------------------------------
// Effect breakdown renderer
// ---------------------------------------------------------------------------

type EffectLineData = {
  icon: string;
  colorClass: string | undefined;
  text: string;
  targetLabel?: string;
};

/** R9 #6/#10 — Plain-English effect descriptions.
 *  Maps every CardEffectKind to a human-readable sentence.
 *  No raw kind names, no coding jargon in output. */
function humanizeEffect(effect: CardEffect): EffectLineData {
  const target = effect.target;
  const val = effect.valueInt ?? 0;
  const abs = Math.abs(val);

  // Helper: format a target phrase when needed
  function targetPhrase(): string {
    switch (target) {
      case "Self":
        return "you";
      case "OpponentChosen":
        return "a chosen opponent";
      case "AllOpponents":
        return "all opponents";
      case "AllPlayers":
        return "everyone";
      case "NearestPlayer":
        return "the nearest player";
      case "Global":
        return "the world";
      default:
        return target;
    }
  }

  switch (effect.kind) {
    // ── Resource deltas ───────────────────────────────────────────────────────
    case "TokenDelta": {
      if (val < 0) {
        const who = target === "Self" ? "You" : `${targetPhrase()} loses`;
        return {
          icon: "▼",
          colorClass: styles.efLoss,
          text: `${who}${target === "Self" ? " lose" : ""} ${abs} token${abs !== 1 ? "s" : ""}`,
        };
      }
      return {
        icon: "▲",
        colorClass: styles.efGain,
        text: `Gain ${val} token${val !== 1 ? "s" : ""}`,
      };
    }

    case "EnergyDelta": {
      if (val < 0) {
        return {
          icon: "▼",
          colorClass: styles.efLoss,
          text: `Lose ${abs} energy cube${abs !== 1 ? "s" : ""}`,
        };
      }
      return {
        icon: "⚡",
        colorClass: styles.efGain,
        text: `Gain ${val} energy cube${val !== 1 ? "s" : ""}`,
      };
    }

    case "PollutionDelta": {
      if (val > 0) {
        return {
          icon: "☠",
          colorClass: styles.efWarn,
          text: `World pollution rises by ${val}`,
        };
      }
      return {
        icon: "🌿",
        colorClass: styles.efGain,
        text: `World pollution drops by ${abs}`,
      };
    }

    // ── Theft / transfer ──────────────────────────────────────────────────────
    case "StealEnergy":
      return {
        icon: "⚡",
        colorClass: styles.efSteal,
        text: `Steal ${val} energy cube${val !== 1 ? "s" : ""} from ${targetPhrase()}`,
      };

    case "StealCard":
      return {
        icon: "🃏",
        colorClass: styles.efSteal,
        text: `Take a card from ${targetPhrase()}'s hand`,
      };

    case "FacilityTransfer":
      return {
        icon: "🏭",
        colorClass: styles.efSteal,
        text: `Transfer a facility from ${targetPhrase()} to you`,
      };

    // ── Destruction ───────────────────────────────────────────────────────────
    case "Destroy":
      return {
        icon: "💥",
        colorClass: styles.efLoss,
        text: `Destroy a facility owned by ${targetPhrase()}`,
      };

    case "Decommission": {
      const cat = effect.categoryFilter?.[0];
      const catNote = cat ? ` (${cat})` : "";
      return {
        icon: "🔧",
        colorClass: styles.efWarn,
        text: `Decommission${catNote} a facility — owner gets 50% cost refund`,
      };
    }

    // ── Passive / aura effects ────────────────────────────────────────────────
    case "AuraApply": {
      const scope = effect.auraScope ?? "effect";
      const cat = effect.categoryFilter?.[0];
      const scopeDesc: Record<string, string> = {
        BuildCost: "reduces your build costs",
        Toll: "modifies tolls",
        Energy: "modifies energy gain/loss",
        Pollution: "modifies pollution",
        TradeBonus: "adds a trade bonus",
        IgnoreEraPollution: "ignores era pollution penalties",
      };
      const scopeText = scopeDesc[scope] ?? `applies ${scope} effect`;
      return {
        icon: "◑",
        colorClass: styles.efAura,
        text: `Passive: ${scopeText}${cat ? ` for ${cat} facilities` : ""}`,
        targetLabel: "STAYS IN HAND",
      };
    }

    case "BuildCostMod": {
      const pending = effect.pendingKind;
      const pendingDesc: Record<string, string> = {
        NextBuildFree: "Your next build is FREE",
        NextSolarFree: "Your next Solar build is FREE",
        NextBuildDiscount: `Your next build costs ${abs} fewer tokens`,
        NextNuclearDiscount: `Your next Nuclear build costs ${abs} fewer tokens`,
        NextPaidDrawFree: "Your next paid card draw is FREE",
        HalveNextTokenLoss: "Halves your next token loss (toll, event, or disaster)",
        NextBuildCostBump: `Opponent's next build costs ${abs} more tokens`,
      };
      const text = pending
        ? (pendingDesc[pending] ?? `Build cost modified (${pending})`)
        : "Modifies build cost";
      return {
        icon: "🏗",
        colorClass: styles.efAura,
        text,
        targetLabel: "STAYS IN HAND",
      };
    }

    case "Immunity": {
      const kinds = effect.immuneAgainstKinds ?? [];
      const kindMap: Record<string, string> = {
        StealFacility: "facility theft",
        Destroy: "destruction",
        Decommission: "decommission",
        StealEnergy: "energy theft",
        DiceOutcome: "dice attacks",
        RngBranch: "random disasters",
        StealCard: "card theft",
      };
      const humanKinds = kinds.map((k) => kindMap[k] ?? k).join(", ");
      return {
        icon: "🛡",
        colorClass: styles.efAura,
        text: `Immune to: ${humanKinds || "all attacks"}`,
        targetLabel: "STAYS IN HAND",
      };
    }

    case "CancelDisaster":
      return {
        icon: "🛡",
        colorClass: styles.efAura,
        text: `Cancels the next natural disaster card (${val} charge${val !== 1 ? "s" : ""})`,
        targetLabel: "STAYS IN HAND",
      };

    // ── Dice / chance ─────────────────────────────────────────────────────────
    case "DiceOutcome":
      return {
        icon: "🎲",
        colorClass: styles.efWarn,
        text: `Roll a die — result determines what happens to ${targetPhrase()}`,
      };

    case "RngBranch": {
      const pct = effect.chancePct ?? 0;
      const cat = effect.categoryFilter?.[0];
      return {
        icon: "🎲",
        colorClass: styles.efWarn,
        text: `${pct}% chance${cat ? ` for ${cat} facilities` : ""} — random outcome`,
      };
    }

    // ── Card draw / hand ──────────────────────────────────────────────────────
    case "DrawCard": {
      const pool = effect.cardPoolFilter?.[0] ?? "Policy";
      return {
        icon: "🃏",
        colorClass: styles.efGain,
        text: `Draw a ${pool} card for free`,
      };
    }

    case "DiscardHand":
      return {
        icon: "🗑",
        colorClass: styles.efLoss,
        text: `${targetPhrase() === "you" ? "Discard your entire hand" : `${targetPhrase()} must discard their hand`}`,
      };

    // ── Trigger (reactive) ────────────────────────────────────────────────────
    case "Trigger": {
      const on = effect.triggerOn ?? "unknown event";
      const onDesc: Record<string, string> = {
        AfterStealEnergyTargetingMe: "someone steals energy from you",
        AfterFacilityDestroyedMine: "one of your facilities is destroyed",
        AfterFacilityDecommissionedMine: "one of your facilities is decommissioned",
        AfterTollPaidByOpponentToMe: "an opponent pays you toll",
      };
      return {
        icon: "⚡",
        colorClass: styles.efAura,
        text: `Reacts when: ${onDesc[on] ?? on}`,
        targetLabel: "STAYS IN HAND",
      };
    }

    // ── Conditional ───────────────────────────────────────────────────────────
    case "Conditional":
      return {
        icon: "?",
        colorClass: styles.efNeutral,
        text: "Effect depends on game conditions",
      };

    // ── TollMod ───────────────────────────────────────────────────────────────
    case "TollMod":
      return {
        icon: "🛣",
        colorClass: styles.efWarn,
        text:
          val >= 0
            ? `Toll for ${targetPhrase()} increases by ${val}`
            : `Toll for ${targetPhrase()} decreases by ${abs}`,
      };

    // ── TurnBlock ─────────────────────────────────────────────────────────────
    // R20-GD-1: Drought blocks the playing seat from building Hydro for N turns.
    case "TurnBlock": {
      const cats = effect.categoryFilter ?? [];
      const catLabel = cats.length > 0 ? cats.join("/") : "facility";
      const turns = val > 0 ? val : 3;
      return {
        icon: "🚫",
        colorClass: styles.efWarn,
        text: `You cannot build ${catLabel} facilities for ${turns} turn${turns !== 1 ? "s" : ""}`,
      };
    }

    default: {
      // Safety fallback — shows kind in plain English style, no coding jargon
      const kindStr = String(effect.kind);
      return {
        icon: "›",
        colorClass: styles.efNeutral,
        text: kindStr
          .replace(/([A-Z])/g, " $1")
          .trim()
          .toLowerCase(),
      };
    }
  }
}

function EffectBreakdown({ effects }: { effects: CardEffect[] }): React.ReactElement {
  const { t } = useI18n();
  const lines: EffectLineData[] = effects.map(humanizeEffect);

  return (
    <div className={styles.effectBreakdown}>
      <div className={styles.effectHeader}>{t("cardDetail.whatWillHappen")}</div>
      {lines.map((line, i) => (
        <div
          key={`${line.icon}-${i}`}
          className={[styles.effectLine, line.colorClass ?? ""].filter(Boolean).join(" ")}
        >
          <span className={styles.effectIcon}>{line.icon}</span>
          <span>
            {line.text}
            {line.targetLabel !== undefined && (
              <span className={styles.targetChip}>{line.targetLabel}</span>
            )}
          </span>
        </div>
      ))}
      {/* Discard/duration conditional line */}
      {effects[0] !== undefined &&
        effects[0].durationMode !== undefined &&
        effects[0].durationMode !== "Instant" && (
          <div className={[styles.effectLine, styles.efCond].join(" ")}>
            <span className={styles.effectIcon}>›</span>
            <span>Duration: {effects[0].durationMode}</span>
          </div>
        )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CardDetailModal — public component
// ---------------------------------------------------------------------------

export function CardDetailModal(): React.ReactElement | null {
  const detailModalCard = useGameStore((s) => s.detailModalCard);
  const modalRevealState = useGameStore((s) => s.modalRevealState);
  // R20-GE-6: when cardReviewBeatActive, CDM is open in read-only mode (no play buttons).
  // A decision modal will follow after the beat completes.
  const cardReviewBeatActive = useGameStore((s) => s.cardReviewBeatActive);
  const tokens = useGameStore((s) => s.tokens);
  const energy = useGameStore((s) => s.energy);
  const currentSeat = useGameStore((s) => s.currentSeat);
  const peqState = useGameStore((s) => s.peqState);
  // WEB.4.R2.CF: detect draw-buy mode (pendingDrawCard === detailModalCard)
  const pendingDrawCard = useGameStore((s) => s.pendingDrawCard);

  const flipReveal = useGameStore((s) => s.flipReveal);
  const closeCardDetail = useGameStore((s) => s.closeCardDetail);
  const clearCardReviewBeat = useGameStore((s) => s.clearCardReviewBeat);
  const playCard = useGameStore((s) => s.playCard);
  const buyPendingCard = useGameStore((s) => s.buyPendingCard);
  const discardPendingCard = useGameStore((s) => s.discardPendingCard);
  // R3 Bug 9b — track whether modal was opened from HandPanel (card already owned)
  const isFromHand = useGameStore((s) => s.detailModalCardFromHand);
  // R5 #4: free-draw acknowledge flow
  const pendingFreeDrawCard = useGameStore((s) => s.pendingFreeDrawCard);
  const acceptFreeDrawCard = useGameStore((s) => s.acceptFreeDrawCard);
  const discardHandCard = useGameStore((s) => s.discardHandCard);

  // WEB.4.R2.CF HIGH 1 (Codex R2): detect draw-buy mode early — needed by
  // handleKeyDown dep array (hooks must reference stable values, not computed-later vars).
  // pendingDrawCard is available from store subscription above.
  const isDrawBuyMode =
    pendingDrawCard !== null &&
    detailModalCard !== null &&
    pendingDrawCard.instanceId === detailModalCard.instanceId;

  // Local: transient flip-flourish state. Plays rotateY keyframe for 500ms,
  // then triggers the actual state toggle to 'revealed'. Codex R1 LOW 2 fix.
  const [isFlipping, setIsFlipping] = useState(false);
  const handleCoverClick = useCallback(() => {
    if (isFlipping) return;
    setIsFlipping(true);
    setTimeout(() => {
      flipReveal();
      setIsFlipping(false);
    }, 250); // Mid-flip swap (50% keyframe) feels natural; full anim is 500ms
  }, [isFlipping, flipReveal]);

  // HIGH 1 (Codex R2): In draw-buy mode, close paths must not silently leave
  // pendingDrawCard set. Route all close attempts through this helper —
  // confirm before discarding so the player doesn't lose a card by accident.
  const attemptCloseDrawBuy = useCallback(
    (cardName: string) => {
      const ok = window.confirm(`Skip ${cardName}? It'll be gone.`);
      if (ok) discardPendingCard();
      // If cancelled → leave modal open, do nothing
    },
    [discardPendingCard],
  );

  // Esc key → close (guard draw-buy mode)
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // R9 codex MED 3: Esc on pending-free-draw must route through confirm
      // discard so the NPC interaction marker isn't orphaned (Leave Tile gate
      // depends on it). Otherwise the draw stays pending forever.
      if (pendingFreeDrawCard !== null) {
        const ok = window.confirm("Discard this card?");
        if (ok) discardPendingCard();
        return;
      }
      if (isDrawBuyMode) {
        const cDef = detailModalCard !== null ? getCardById(detailModalCard.id) : undefined;
        attemptCloseDrawBuy(cDef?.cardName ?? "this card");
      } else {
        closeCardDetail();
      }
    },
    [
      pendingFreeDrawCard,
      discardPendingCard,
      isDrawBuyMode,
      detailModalCard,
      attemptCloseDrawBuy,
      closeCardDetail,
    ],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  if (detailModalCard === null) return null;

  const cardDef = getCardById(detailModalCard.id);
  if (cardDef === undefined) return null;

  const isRevealed = modalRevealState === "revealed";
  const cardType = cardDef.cardType;
  const buyCost = cardDef.buyCost;
  // WEB.4.UI.15 R1 MED (Codex): some defensive cards use costCurrency=EnergyCube
  // (UN_Green_Pact_Support / Microgrids / Disaster_Relief_Fund). Treat them as
  // energy-priced; default to tokens. Cost display + affordability gate honor
  // the actual currency.
  const costCurrency: "Token" | "EnergyCube" =
    cardDef.costCurrency === "EnergyCube" ? "EnergyCube" : "Token";
  const seatTokens = tokens[currentSeat] ?? 0;
  const seatEnergy = energy[currentSeat] ?? 0;
  const balanceForCost = costCurrency === "EnergyCube" ? seatEnergy : seatTokens;
  const canAfford = balanceForCost >= buyCost;
  const isImmediate = cardDef.isImmediate === true;
  const isPlayable = _isCardPlayable(cardDef.effects); // codex R1 HIGH 2/3 — disable Pay/Play for deferred cards
  // R7 #5/#7: use resolveArtPath instead of naive /images/cards/<artwork>.jpg
  // so numeric/source-subfolder artwork values resolve correctly.
  const artPath = resolveArtPath(cardDef.artwork, cardType);
  const coverPath = getCoverPath(cardType);
  const showBoonOverlay = isBoonEvent(cardType, cardDef.name);

  // Educational fact — suppress placeholder text AND require citation marker.
  // codex fact-check audit 2026-05-11 HIGH 3: previously any non-TODO fact would
  // render, but several existing facts contain uncited advocacy (Nuclear_Fusion
  // "near-limitless clean energy", Perovskite "dominant energy source"). The
  // fun-fact-authoring-brief.md REQUIRES every fact to include a citation
  // ("(Source: ...)"). Gate facts behind that marker.
  const _factText = cardDef.educationalFact ?? "";
  const _hasCitation = _factText.includes("Source:") || _factText.includes("(Source");
  const eduFact =
    _factText && !_factText.startsWith("(Authoring TODO") && _hasCitation
      ? cardDef.educationalFact
      : null;

  // Art gradient class
  const artGradientClass =
    cardType === "Event"
      ? styles.artGradientEvent
      : cardType === "Policy"
        ? styles.artGradientPolicy
        : cardType === "Mitigate"
          ? styles.artGradientMitigate
          : styles.artGradientTech;

  // Type tag / badge classes
  const typeSuffix =
    cardType === "Event"
      ? "Event"
      : cardType === "Policy"
        ? "Policy"
        : cardType === "Mitigate"
          ? "Mitigate"
          : "Tech";

  const badgeClass = styles[`badge${typeSuffix}` as keyof typeof styles] ?? "";
  const tagClass = styles[`tag${typeSuffix}` as keyof typeof styles] ?? "";

  // Footer button layout
  type FooterMode =
    | "acknowledge" // Event isImmediate only
    | "play-cancel" // Event !isImmediate, Policy/Tech buyCost=0 (not from hand)
    | "pay-cancel" // Policy buyCost > 0, from hand
    | "draw-buy-discard" // Policy/Mitigate drawn from NPC — always forced even buyCost=0
    | "hand-use-only" // R3 Bug 9b: card already in hand — no Buy/Discard ever
    | "draw-free-acknowledge" // R5 #4: free drawn card — Take Card | Discard
    | "hand-aura-passive" // R5 #5+#14: aura/passive card in hand — How to Use + Discard
    | "review-beat"; // R20-GE-6: OR-choice card review beat — read-only, decision follows

  // R9 #6/#10: detect aura/passive cards — expanded set of passive effect kinds.
  // Keep in sync with PASSIVE_EFFECT_KINDS + _isAuraCard() in game-store.ts.
  // AuraApply, BuildCostMod, Immunity, CancelDisaster all auto-apply on hand
  // entry. discardMode=Whenever ALONE no longer marks a card as passive —
  // Climate_Protest (Decommission@OpponentChosen) has discardMode=Whenever but
  // requires a Use click to open TargetPicker. A card is passive only when
  // at least one effect is in PASSIVE_KINDS AND no effect needs active targeting.
  const PASSIVE_KINDS = new Set(["AuraApply", "BuildCostMod", "Immunity", "CancelDisaster"]);
  const ACTIVE_TARGET_KINDS = new Set([
    "Decommission",
    "Destroy",
    "FacilityTransfer",
    "StealCard",
    "StealEnergy",
    "DiceOutcome",
    "GiveCard",
  ]);
  const effs = cardDef.effects ?? [];
  const hasActiveTargeting = effs.some(
    (e) => ACTIVE_TARGET_KINDS.has(e.kind) || e.target === "OpponentChosen",
  );
  const isAuraCard =
    !hasActiveTargeting &&
    (effs.some((e) => PASSIVE_KINDS.has(e.kind)) || cardDef.discardMode === "Whenever");
  // R5 #4: pendingFreeDrawCard signals the NEW free-draw acknowledge flow.
  const isPendingFreeDraw =
    pendingFreeDrawCard !== null &&
    detailModalCard !== null &&
    pendingFreeDrawCard.instanceId === detailModalCard.instanceId;

  let footerMode: FooterMode;
  if (cardReviewBeatActive) {
    // R20-GE-6: card review beat — read-only mode, decision follows after beat.
    footerMode = "review-beat";
  } else if (isPendingFreeDraw) {
    // R5 #4: free drawn card — show acknowledge popup BEFORE entering hand.
    footerMode = "draw-free-acknowledge";
  } else if (isFromHand && isAuraCard) {
    // R5 #5/#14: aura/passive card in hand — auto-fires on entry, no Use button.
    footerMode = "hand-aura-passive";
  } else if (isFromHand) {
    // R3 Bug 9b: already-owned non-aura card — Use/Play path, no Buy/Discard.
    footerMode = "hand-use-only";
  } else if (cardType === "Event" && isImmediate) {
    footerMode = "acknowledge";
  } else if (cardType === "Mitigate") {
    // R3 Bug 7: Mitigate cards ALWAYS go through buy-or-discard — even buyCost=0.
    footerMode = "draw-buy-discard";
  } else if (buyCost > 0 && isDrawBuyMode) {
    // WEB.4.R2.CF Case B: drawn paid card — Buy acquires, Discard loses it
    footerMode = "draw-buy-discard";
  } else if (buyCost > 0) {
    footerMode = "pay-cancel";
  } else {
    footerMode = "play-cancel";
  }

  // Click outside scrim to close.
  // HIGH 1 fix: in draw-buy mode, backdrop click routes through confirmed discard,
  // not a silent closeCardDetail (which would orphan pendingDrawCard).
  // R9 codex MED 3: same gate for pending-free-draw mode — otherwise the
  // Event/Policy NPC interaction marker stays unset and Leave Tile is blocked.
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (isPendingFreeDraw) {
      const ok = window.confirm("Discard this card?");
      if (ok) discardPendingCard();
      return;
    }
    if (isDrawBuyMode) {
      attemptCloseDrawBuy(cardDef.cardName);
    } else {
      closeCardDetail();
    }
  };

  // ── COVER STATE ──
  if (!isRevealed) {
    return (
      // biome-ignore lint/a11y/useKeyWithClickEvents: overlay scrim — keyboard handled via Esc listener
      // biome-ignore lint/a11y/useSemanticElements: <dialog> resets stacking context; custom overlay needed for z-index layering
      <div
        className={styles.overlay}
        onClick={handleOverlayClick}
        role="dialog"
        aria-modal="true"
        aria-label="Card detail"
        data-testid="modal-overlay"
      >
        <div
          className={[styles.panel, styles.panelCover, isFlipping ? styles.panelFlipping : ""]
            .filter(Boolean)
            .join(" ")}
          data-testid="modal-panel-cover"
        >
          <button
            type="button"
            className={styles.closeBtn}
            onClick={(e) => {
              e.stopPropagation();
              // HIGH 1 fix: in draw-buy mode, X routes through confirmed discard.
              if (isDrawBuyMode) {
                attemptCloseDrawBuy(cardDef.cardName);
              } else {
                closeCardDetail();
              }
            }}
            aria-label="Close"
          >
            ✕
          </button>
          <button
            type="button"
            className={styles.coverClickTarget}
            onClick={handleCoverClick}
            aria-label="Click to reveal card"
            data-testid="modal-cover-click-target"
          >
            <img
              className={styles.coverImg}
              src={coverPath}
              alt={`${cardType} cover`}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
            {showBoonOverlay && (
              <div
                className={styles.boonOverlay}
                data-testid="cover-boon-overlay"
                aria-label="Positive event — beneficial mechanic"
              >
                <span className={styles.boonIcon}>+</span>
                <span className={styles.boonLabel}>BOON</span>
              </div>
            )}
          </button>
        </div>
      </div>
    );
  }

  // ── REVEALED STATE ──
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: overlay scrim — keyboard handled via Esc listener
    // biome-ignore lint/a11y/useSemanticElements: <dialog> resets stacking context; custom overlay needed for z-index layering
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Card detail"
      data-testid="modal-overlay"
    >
      <div
        className={[styles.panel, styles.panelRevealed].join(" ")}
        data-testid="modal-panel-revealed"
      >
        <button
          type="button"
          className={styles.closeBtn}
          onClick={() => {
            // HIGH 1 fix: in draw-buy mode, X routes through confirmed discard.
            if (isDrawBuyMode) {
              attemptCloseDrawBuy(cardDef.cardName);
            } else {
              closeCardDetail();
            }
          }}
          aria-label="Close"
        >
          ✕
        </button>

        {/* LEFT: Card Art */}
        <div className={[styles.art, artGradientClass].join(" ")} data-testid="modal-art">
          {artPath !== null ? (
            <img
              className={styles.artImg}
              src={artPath}
              alt={`${cardDef.cardName} card art`}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <div className={styles.artPlaceholder}>[ {cardDef.cardName} — no art file ]</div>
          )}
          <div className={[styles.artTypeBadge, badgeClass].join(" ")}>
            {typeSuffix.toUpperCase()}
          </div>
          <div className={styles.artEraTag}>Era {cardDef.era}</div>
        </div>

        {/* RIGHT: Body (scrollable) */}
        <div className={styles.modalBody} data-testid="modal-body">
          <div className={styles.nameRow}>
            <div className={styles.cardName}>{cardDef.cardName}</div>
            <div className={[styles.typeTag, tagClass].join(" ")}>{typeSuffix.toUpperCase()}</div>
          </div>

          {cardDef.description && <div className={styles.description}>{cardDef.description}</div>}

          {cardDef.effects.length > 0 && <EffectBreakdown effects={cardDef.effects} />}

          {buyCost > 0 && (
            <>
              <div
                className={[styles.costLine, canAfford ? "" : styles.costInsufficient].join(" ")}
              >
                <div className={styles.costCoinIcon} />
                Cost: {buyCost} {costCurrency === "EnergyCube" ? "Energy" : "Tokens"}
              </div>
              {!canAfford && (
                <div className={styles.affordHint}>
                  (insufficient — you have {balanceForCost}{" "}
                  {costCurrency === "EnergyCube" ? "energy" : "tokens"})
                </div>
              )}
            </>
          )}

          {eduFact !== null && (
            <div className={styles.eduFact}>
              <span className={styles.eduFactLabel}>DID YOU KNOW</span>
              {eduFact}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className={styles.divider} />

        {/* Footer buttons */}
        {footerMode === "acknowledge" && (
          <div
            className={[styles.footer, styles.footerSingle].join(" ")}
            data-testid="modal-footer"
          >
            <button
              type="button"
              className={[
                styles.modalBtn,
                canAfford && isPlayable ? styles.btnUse : styles.btnDisabled,
              ].join(" ")}
              data-testid="btn-acknowledge"
              disabled={!canAfford || !isPlayable}
              onClick={() => {
                if (canAfford && isPlayable) {
                  playSfx("card-play");
                  playCard(detailModalCard);
                }
              }}
              title={
                !isPlayable
                  ? "Card effects not yet supported (WEB.4.UI.4+)"
                  : !canAfford
                    ? costCurrency === "EnergyCube"
                      ? "Not enough energy"
                      : "Not enough tokens"
                    : undefined
              }
            >
              ✓ Acknowledge
            </button>
          </div>
        )}

        {footerMode === "play-cancel" && (
          <div className={[styles.footer, styles.footerDual].join(" ")} data-testid="modal-footer">
            <button
              type="button"
              className={[styles.modalBtn, isPlayable ? styles.btnPlay : styles.btnDisabled].join(
                " ",
              )}
              data-testid="btn-play"
              disabled={!isPlayable}
              onClick={() => {
                if (isPlayable) playCard(detailModalCard);
              }}
              title={!isPlayable ? "Card effects not yet supported (WEB.4.UI.4+)" : undefined}
            >
              ▶ Play
            </button>
            <button
              type="button"
              className={[styles.modalBtn, styles.btnCancel].join(" ")}
              data-testid="btn-cancel"
              onClick={closeCardDetail}
            >
              Cancel
            </button>
          </div>
        )}

        {footerMode === "pay-cancel" && (
          <div className={[styles.footer, styles.footerDual].join(" ")} data-testid="modal-footer">
            <button
              type="button"
              className={[
                styles.modalBtn,
                canAfford && isPlayable ? styles.btnPlay : styles.btnDisabled,
              ].join(" ")}
              data-testid="btn-pay"
              disabled={!canAfford || !isPlayable}
              onClick={() => {
                if (canAfford && isPlayable) playCard(detailModalCard);
              }}
              title={
                !isPlayable
                  ? "Card effects not yet supported (WEB.4.UI.4+)"
                  : !canAfford
                    ? costCurrency === "EnergyCube"
                      ? "Not enough energy"
                      : "Not enough tokens"
                    : undefined
              }
            >
              💰 Pay {buyCost}
              {costCurrency === "EnergyCube" ? " energy" : " tokens"}
            </button>
            <button
              type="button"
              className={[styles.modalBtn, styles.btnCancel].join(" ")}
              data-testid="btn-cancel"
              onClick={closeCardDetail}
            >
              Cancel
            </button>
          </div>
        )}

        {/* R3 Bug 9b: Hand-use-only — card already in hand. No Buy/Discard. */}
        {footerMode === "hand-use-only" && (
          <div className={[styles.footer, styles.footerDual].join(" ")} data-testid="modal-footer">
            <button
              type="button"
              className={[styles.modalBtn, isPlayable ? styles.btnPlay : styles.btnDisabled].join(
                " ",
              )}
              data-testid="btn-use"
              disabled={!isPlayable}
              onClick={() => {
                if (isPlayable) playCard(detailModalCard);
              }}
              title={!isPlayable ? "Card effects not yet supported (WEB.4.UI.4+)" : undefined}
            >
              ▶ Use
            </button>
            <button
              type="button"
              className={[styles.modalBtn, styles.btnCancel].join(" ")}
              data-testid="btn-close-hand"
              onClick={closeCardDetail}
            >
              Close
            </button>
          </div>
        )}

        {/* R5 #4: Free-drawn card acknowledge popup — Take Card adds to hand
            (and auto-registers aura if applicable); Discard drops the card. */}
        {footerMode === "draw-free-acknowledge" && (
          <div className={[styles.footer, styles.footerDual].join(" ")} data-testid="modal-footer">
            <button
              type="button"
              className={[styles.modalBtn, styles.btnPlay].join(" ")}
              data-testid="btn-take-card"
              onClick={() => {
                acceptFreeDrawCard();
              }}
            >
              ✓ Take Card
            </button>
            <button
              type="button"
              className={[styles.modalBtn, styles.btnCancel].join(" ")}
              data-testid="btn-discard-free"
              onClick={() => {
                const ok = window.confirm("Discard this card?");
                if (ok) discardPendingCard();
              }}
            >
              Discard
            </button>
          </div>
        )}

        {/* R5 #5/#14: Aura/passive card in hand — auto-applied on hand entry.
            No Use button (aura is already active). "How to Use" text explains
            the effect; Discard removes the aura. */}
        {footerMode === "hand-aura-passive" && (() => {
          // Discard-lock invariant (generalised — replaces the old hardcoded
          // SELF_PENALTY_LOCKED name list). A passive card cannot be discarded while
          // it still has a live SELF-PENALTY entry pending in the owner's own PEQ
          // bucket, matched by instanceId. This blocks dodging a next-build penalty
          // (Logistics_Disruption / Worker_Union_Strikes → NextBuildCostBump) by
          // discarding before the next build drains it.
          //
          // Only PENALTY pending-kinds lock. Benefit/discount entries
          // (NextBuildFree / NextBuildDiscount / NextSolarFree / ...) also carry an
          // instanceId but must NOT lock — discarding a benefit only hurts the holder,
          // so dumping an unwanted discount card stays allowed. Cards with no PEQ entry
          // at all (e.g. Ocean_Acidification, a permanent self-aura) are freely
          // discardable like any other aura. Once a penalty fires, the card
          // auto-discards (see game-store buildFacility consumed-instanceId sweep).
          const PENALTY_PENDING_KINDS = new Set<string>(["NextBuildCostBump"]);
          const cardInstanceId = detailModalCard?.instanceId ?? "";
          const instIdNum = Number.isFinite(Number(cardInstanceId))
            ? Math.abs(Math.floor(Number(cardInstanceId)))
            : 0;
          const seatBucket = peqState?.queues?.[String(currentSeat)] ?? [];
          const penaltyStillPending =
            instIdNum > 0 &&
            seatBucket.some(
              (pe: { instanceId?: number; kind?: string }) =>
                pe.instanceId === instIdNum && PENALTY_PENDING_KINDS.has(pe.kind ?? ""),
            );
          const discardDisabled = penaltyStillPending;
          return (
          <div className={styles.footer} data-testid="modal-footer">
            <div className={styles.howToUse} data-testid="aura-how-to-use">
              <strong>{discardDisabled ? "Penalty pending:" : "How to Use:"}</strong>{" "}
              {discardDisabled
                ? `${cardDef.description ?? "Effect will resolve automatically."} This penalty cannot be discarded until it resolves.`
                : (cardDef.description ?? "This card's effects are applied automatically while it remains in your hand.")}
            </div>
            <button
              type="button"
              className={[styles.modalBtn, discardDisabled ? styles.btnDisabled : styles.btnCancel].join(" ")}
              data-testid="btn-discard-aura"
              disabled={discardDisabled}
              title={discardDisabled ? "This penalty must resolve before discarding" : undefined}
              onClick={() => {
                if (discardDisabled) return;
                const ok = window.confirm("Discard this aura card? Its effects will end.");
                if (ok && detailModalCard) discardHandCard(detailModalCard);
              }}
            >
              Discard
            </button>
            <button
              type="button"
              className={[styles.modalBtn, styles.btnCancel].join(" ")}
              data-testid="btn-close-aura"
              onClick={closeCardDetail}
            >
              Close
            </button>
          </div>
          );
        })()}

        {/* R21-GE-8: Card review beat — player clicks "MAKE DECISION" when ready.
            No auto-advance timer. Button calls clearCardReviewBeat() → CDM closes
            → OrChoiceModal opens via existing pendingOrChoice gate. */}
        {footerMode === "review-beat" && (
          <div
            className={[styles.footer, styles.footerSingle].join(" ")}
            data-testid="modal-footer"
          >
            <button
              type="button"
              className={[styles.modalBtn, styles.btnPlay].join(" ")}
              data-testid="btn-make-decision"
              onClick={clearCardReviewBeat}
            >
              ▶ MAKE DECISION
            </button>
          </div>
        )}

        {/* WEB.4.R2.CF Case B: Draw-buy mode — card not yet in hand. Buy acquires; Discard loses. */}
        {footerMode === "draw-buy-discard" && (
          <div className={[styles.footer, styles.footerDual].join(" ")} data-testid="modal-footer">
            <button
              type="button"
              className={[styles.modalBtn, canAfford ? styles.btnPlay : styles.btnDisabled].join(
                " ",
              )}
              data-testid="btn-buy"
              disabled={!canAfford}
              onClick={() => {
                if (canAfford) buyPendingCard();
              }}
              title={
                !canAfford
                  ? costCurrency === "EnergyCube"
                    ? "Not enough energy"
                    : "Not enough tokens"
                  : undefined
              }
            >
              🛒 Buy {buyCost}
              {costCurrency === "EnergyCube" ? " energy" : " tokens"}
            </button>
            <button
              type="button"
              className={[styles.modalBtn, styles.btnCancel].join(" ")}
              data-testid="btn-discard-draw"
              onClick={() => {
                // Confirmation: irreversible — card will be gone
                const ok = window.confirm(`Skip ${cardDef.cardName}? It'll be gone.`);
                if (ok) discardPendingCard();
              }}
            >
              Discard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
